import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InvitationPreviewV2 from '../_components/InvitationPreview/InvitationPreviewV2';
import { INVITATION_TEMPLATES_V2 } from '../_data/templates';
import { InvitationDesign } from '../_types/invitation-design-v2';
import { Event } from '../_graphql/generated/operations';
import { CARD_HEIGHT, CARD_WIDTH, measureCard, CardMetrics } from '../_render/measure';

// THE RENDER TARGET — the page a headless browser drives.
//
// It exists because no static check can see a card. Twelve of the fifteen 2026
// templates were measurably wrong before anyone looked at them, and the checks
// that catch that all need layout: content slack, line wrapping, whether a
// background loaded at all, whether a font is really there.
//
// Two deliberate properties:
//
//   Cards render at DESIGN SIZE, 528x720, never scaled. /gallery and /proof
//   scale with a transform, which keeps line breaks honest for a human but
//   would put fractional pixels under every measurement here.
//
//   Designs arrive over the CONTROL SURFACE (window.__render), not only from
//   the library. An agent-authored design that is not a file yet — a variant
//   being trialled, a customer's own card — measures identically to a shipped
//   one. A query parameter would have capped that at the URL length.
//
// The page is unauthenticated and reads no data, like /gallery beside it.

const stubEvent = {
  id: 'render',
  eventName: 'Your Event',
  eventDateTime: Date.now(),
  location: 'Your Venue',
} as unknown as Event;

declare global {
  interface Window {
    /** Replace what is on the page. Resolves when it has been laid out. */
    __render: (designs: InvitationDesign[], opts?: RenderOptions) => Promise<string[]>;
    /** Measure everything currently on the page.
     *
     *  ASYNC, and it has to be: one metric — whether a declared image ground
     *  actually loaded — can only be answered by asking the image pipeline, and
     *  that answer arrives on a promise. Callers await it. */
    __measureAll: () => Promise<CardMetrics[]>;
    /** Hide the type so a screenshot captures the GROUND alone — the driver
     *  samples that image for contrast, which it cannot get from the DOM. */
    __setTextVisible: (visible: boolean) => void;
    /** Set once the first paint has happened, for a driver with no other clock. */
    __renderReady: boolean;
  }
}

interface RenderOptions {
  /** Cards per row. 1 (default) stacks them; more makes a contact sheet. */
  columns?: number;
  /** Gap and page margin in px. */
  gap?: number;
  /** Page background behind the cards. */
  pageBackground?: string;
  /** Show each card's id under it — for a sheet a human reads, not a measure. */
  labels?: boolean;
}

const DEFAULTS: Required<RenderOptions> = {
  columns: 1,
  gap: 32,
  pageBackground: '#ffffff',
  labels: false,
};

const RenderTarget: React.FC = () => {
  const [params] = useSearchParams();
  const [designs, setDesigns] = useState<InvitationDesign[] | null>(null);
  const [opts, setOpts] = useState<Required<RenderOptions>>(DEFAULTS);
  const [textVisible, setTextVisible] = useState(true);

  // The library, by id, so ?template= and the control surface agree.
  const byId = useMemo(() => {
    const m = new Map<string, InvitationDesign>();
    for (const d of INVITATION_TEMPLATES_V2) m.set(d.template.id, d);
    return m;
  }, []);

  // Query parameters are the HAND path — open /render?template=eucalypt in a
  // browser and you get exactly what the driver measures, at full size.
  const fromQuery = useMemo(() => {
    if (params.get('all') !== null) return INVITATION_TEMPLATES_V2;
    const ids = (params.get('templates') ?? params.get('template') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return ids.map((id) => byId.get(id)).filter((d): d is InvitationDesign => !!d);
  }, [params, byId]);

  const render = useCallback(
    (next: InvitationDesign[], options?: RenderOptions) =>
      new Promise<string[]>((resolve) => {
        setOpts({ ...DEFAULTS, ...(options ?? {}) });
        setDesigns(next);
        setTextVisible(true);
        // Two frames, then FONTS, then two more. One frame lets React commit,
        // the second lets the browser lay the committed tree out — resolving
        // before that returns pre-layout zeroes.
        //
        // The font wait is not belt-and-braces. index.html fetches Google
        // Fonts over the network, so a first paint can be in a fallback serif,
        // and every number that matters reads differently there: line counts,
        // content height, slack, and the fallback metric itself. Resolving on
        // layout alone hands back plausible wrong numbers, which is the worst
        // kind. Twice, because a second render can ask for a family the first
        // never did, and `fonts.ready` settles against the faces known when it
        // was called.
        const settle = () =>
          new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        void settle()
          .then(() => document.fonts.ready)
          .then(settle)
          .then(() => document.fonts.ready)
          .then(() => resolve(next.map((d) => d.template.id)));
      }),
    [],
  );

  const measureAll = useCallback((): Promise<CardMetrics[]> => {
    const cards = [...document.querySelectorAll<HTMLElement>('[data-card-id]')];
    return Promise.all(
      cards.map((card) => {
        const root = card.querySelector<HTMLElement>('.invitation-preview-content');
        const design = (card as any).__design as InvitationDesign;
        if (!root) throw new Error(`no rendered card for ${card.dataset.cardId}`);
        return measureCard(root, design);
      }),
    );
  }, []);

  useEffect(() => {
    window.__render = render;
    window.__measureAll = measureAll;
    window.__setTextVisible = setTextVisible;
    window.__renderReady = true;
  }, [render, measureAll]);

  const shown = designs ?? fromQuery;

  return (
    <div
      data-render-target
      style={{
        minHeight: '100vh',
        background: opts.pageBackground,
        padding: opts.gap,
        display: 'grid',
        gridTemplateColumns: `repeat(${opts.columns}, ${CARD_WIDTH}px)`,
        gap: opts.gap,
        justifyContent: 'start',
        alignItems: 'start',
      }}
    >
      {/* Hiding the type rather than removing it keeps the layout identical,
          so the ground screenshot lines up pixel-for-pixel with the measured
          row boxes. Removing it would let the card reflow underneath them. */}
      <style>{textVisible ? '' : '.invitation-preview-rows { visibility: hidden; }'}</style>
      {shown.map((design, i) => (
        <div
          key={`${design.template.id}-${i}`}
          data-card-id={design.template.id}
          data-card-index={i}
          ref={(el) => {
            // The design that produced this card, reachable from the DOM node,
            // so measurement compares rendered against DECLARED rather than
            // re-deriving what it is looking at.
            if (el) (el as any).__design = design;
          }}
          style={{ width: CARD_WIDTH }}
        >
          <div style={{ width: CARD_WIDTH, minHeight: CARD_HEIGHT }}>
            <InvitationPreviewV2 design={design} event={stubEvent} />
          </div>
          {opts.labels && (
            <div
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 13,
                color: '#54524d',
                paddingTop: 10,
                textAlign: 'center',
              }}
            >
              {design.template.name} · {design.template.id}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RenderTarget;
